import { featuredMetrics } from "../../../content";
import type { ResolvedMetric } from "../../../types/content";

/**
 * ============================================================================
 * MetricsBand — the headline figures, directly under the hero.
 * ============================================================================
 *
 * D-071. Closes the `metrics.json` half of the D-065 finding: ten entries sat
 * in the content layer with NO consumer at all, while three source comments
 * asserted that the featured ones "render in the hero strip". They rendered
 * nowhere. This is that strip, and the comments are true now.
 *
 * IT CURATES NOTHING. Which metrics appear is `featured` in metrics.json,
 * decided by owner ruling 2026-08-21, and the label, value and context are the
 * layer's own strings rendered verbatim. That is deliberate and it is the D-065
 * lesson stated the other way round: the certifications section rendered
 * curated display titles over the layer's names, and the result was that
 * "CLSSBB" — a post-nominal its owner carries in their LinkedIn slug — appeared
 * nowhere in the document a recruiter searches. A component that re-words the
 * layer makes the layer unsearchable.
 *
 * THE ORDER IS THE FILE'S ORDER, for the same reason: nothing here decides it.
 *
 * ---------------------------------------------------------------------------
 * FORM: a KPI row of stat tiles, which is what a handful of headline numbers
 * wants — not a chart. Three rules come from that and each is easy to get
 * wrong:
 *
 *   - NO `tabular-nums` on the values. Equal-width digits are for COLUMNS that
 *     must align vertically (the Education year pills, correctly, since D-049).
 *     At display size they make a number like "21+" read loose and mis-set.
 *     Proportional figures are the default and the default is right here.
 *   - The values wear TEXT tokens, never a brand hue. There is no series
 *     identity for a colour to carry — four unrelated measures — so a gradient
 *     on the numbers would be decoration claiming to be information. It also
 *     dodges D-054's trap, where the failing stop of `--grad-brand-*` was not
 *     the one the sweep named.
 *   - No legend, no tooltip, no hover layer: there is no plot to interrogate.
 *
 * `--text-faint` appears nowhere below, also deliberately. It is tuned against
 * `--surface-card` and measures only 4.44:1 on other grounds (D-045), and
 * `.card-face` paints `--surface`. `--text-muted` is the quiet role that
 * survives both.
 * ---------------------------------------------------------------------------
 *
 * Returns null on an empty list rather than an empty section, per II.4: a
 * heading over nothing is a placeholder, and placeholders do not ship.
 */
export default function MetricsBand(): JSX.Element | null {
	if (featuredMetrics.length === 0) {
		return null;
	}

	return (
		<section className="section-shell" aria-labelledby="metrics-band-heading">
			<div className="section-rail">
				{/* The eyebrow labels the section INSTEAD of an <h2>, which is the one
				    structural choice here worth defending. This band sits between the
				    hero's <h1> and the page's first real section, and a heading there
				    would announce a second topic before the first has been read — the
				    numbers ARE the hero's evidence, not a new subject. A <section>
				    may take its accessible name from any element, so the eyebrow that
				    was going to be rendered anyway (D-063: every section carries one)
				    supplies it, and no text is hidden to achieve that. */}
				<p id="metrics-band-heading" className="section-eyebrow">
					Delivery in Numbers
				</p>

				<div className="mt-2 grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-4">
					{featuredMetrics.map((metric: ResolvedMetric) => (
						<div key={metric.id} className="card-shell">
							<div className="card-face flex h-full flex-col p-6">
								<span className="text-3xl font-semibold leading-none text-[var(--text-strong)] sm:text-4xl">
									{metric.value}
								</span>
								<span className="mt-3 text-sm font-semibold text-[var(--text-secondary)]">
									{metric.label}
								</span>
								{/* The context line is not decoration and not optional.
								    Article I.2: "a claim without context is resume
								    material; a claim with context is documentary
								    material." A bare "$3M+" is the former. */}
								<span className="mt-2 text-xs leading-relaxed text-[var(--text-muted)]">
									{metric.context}
								</span>
							</div>
						</div>
					))}
				</div>
			</div>
		</section>
	);
}
